import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ArrowLeft, ChevronDown, FileText, Loader2 } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import {
  documentsKey,
  documentsQueryOptions,
  kindLabel,
  updateEventDocument,
  KIND_OPTIONS,
  type DocumentsPayload,
} from "@/core/documents"
import { DocumentEditor } from "@/components/documents/DocumentEditor"

// Full-screen collaborative document editor — drilled from a Documents row. A
// distinct top segment (/doc/$documentId) keeps the Documents list (/documents)
// reachable, mirroring /p/$pageId for pages. The doc row (incl. yjsRoomId) is
// read from the list query the editor needs nothing more than that.
export const Route = createFileRoute("/$orgSlug/$eventSlug/doc/$documentId")({
  component: DocumentRecordView,
})

function DocumentRecordView() {
  const { orgSlug, eventSlug, documentId } = useParams({
    from: "/$orgSlug/$eventSlug/doc/$documentId",
  })
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const q = useQuery({ ...documentsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const doc = q.data?.documents.find((d) => d.id === documentId)

  // Doc type is edited inline from the header. Optimistically patch the list
  // cache so the label (and the Documents list icon) flip immediately; roll
  // back on failure.
  const kindMut = useMutation({
    mutationFn: (kind: string) => updateEventDocument(documentId, { kind }),
    onMutate: async (kind) => {
      if (!eventId) return
      const key = documentsKey(eventId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<DocumentsPayload>(key)
      if (prev) {
        qc.setQueryData<DocumentsPayload>(key, {
          ...prev,
          documents: prev.documents.map((d) => (d.id === documentId ? { ...d, kind } : d)),
        })
      }
      return { prev }
    },
    onSuccess: (_res, kind) => toast.success(`Type changed to ${kindLabel(kind)}`),
    onError: (err, _kind, ctx) => {
      if (eventId && ctx?.prev) qc.setQueryData(documentsKey(eventId), ctx.prev)
      toast.error(err instanceof ApiError ? err.message : "Couldn't change type")
    },
    onSettled: () => {
      if (eventId) void qc.invalidateQueries({ queryKey: documentsKey(eventId) })
    },
  })

  if (!doc) {
    return (
      <div className="flex h-full items-center justify-center gap-2 bg-white text-[13px] text-plum-700/55 dark:bg-surface dark:text-plum-200/45">
        {q.isError ? (
          <span>{(q.error as Error)?.message ?? "Failed to load document"}</span>
        ) : q.isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading document…
          </>
        ) : (
          <span>Document not found.</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      <div className="flex h-[52px] shrink-0 items-center gap-3 border-b border-plum-200/50 bg-white px-4 dark:border-white/10 dark:bg-surface">
        <Link
          to="/$orgSlug/$eventSlug/documents"
          params={{ orgSlug, eventSlug }}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-plum-700/60 transition hover:bg-plum-100/60 dark:text-plum-200/50 dark:hover:bg-white/5"
          title="Back to documents"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <FileText className="h-4 w-4 shrink-0 text-[var(--accent)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
            {doc.title || "Untitled document"}
          </div>
          <div className="flex min-w-0 items-center text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
            <label className="relative -ml-1 inline-flex shrink-0 items-center rounded px-1 transition hover:bg-plum-100/60 hover:text-plum-900 dark:hover:bg-white/5 dark:hover:text-plum-50">
              <span className="sr-only">Document type</span>
              {kindLabel(doc.kind)}
              <ChevronDown className="ml-0.5 h-3 w-3 opacity-60" />
              <select
                value={doc.kind}
                disabled={kindMut.isPending}
                onChange={(e) => {
                  if (e.target.value !== doc.kind) kindMut.mutate(e.target.value)
                }}
                className="absolute inset-0 cursor-pointer appearance-none opacity-0 disabled:cursor-wait"
                title="Change document type"
              >
                {/* Keep a legacy/unknown kind selectable so the control reflects it. */}
                {!KIND_OPTIONS.some((k) => k.value === doc.kind) && (
                  <option value={doc.kind}>{kindLabel(doc.kind)}</option>
                )}
                {KIND_OPTIONS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <span className="truncate">
            {doc.currentSnapshot
              ? ` · Published v${doc.currentSnapshot.version}`
              : " · Draft"}
            </span>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DocumentEditor
          doc={doc}
          eventId={eventId ?? null}
          onPublished={() => {
            if (eventId) void qc.invalidateQueries({ queryKey: documentsKey(eventId) })
          }}
        />
      </div>
    </div>
  )
}
